import React from 'react';
import { LineChart, XAxis, YAxis, CartesianGrid, Tooltip, Line, ResponsiveContainer, ReferenceArea, Area, ComposedChart, Scatter } from 'recharts';
import {
  calculateAverage,
  calculateTrend,
  getLoudestQuietestFingers,
  prepareTrendOverTimeData,
  prepareTrendOverTempoData,
  prepareTrendOverRepetitionData,
  formatBalance,
  calculateLinearRegression
} from '../../utils/volumeDeltaUtils';

// Helper to calculate standard deviation
const calculateStdDev = (values) => {
  if (values.length === 0) return 0;
  const mean = values.reduce((acc, val) => acc + val, 0) / values.length;
  const squaredDiffs = values.map(val => Math.pow(val - mean, 2));
  const variance = squaredDiffs.reduce((acc, val) => acc + val, 0) / values.length;
  return Math.sqrt(variance);
};

// Helper to calculate symmetric Y-axis range based on regression + std dev
const getSymmetricYDomainForRegression = (regressionData) => {
  if (!regressionData || regressionData.length === 0) return [-10, 10];
  
  let maxAbs = 0;
  regressionData.forEach(series => {
    series.data.forEach(point => {
      const upper = Math.abs(point.value + (series.stdDev || 0));
      const lower = Math.abs(point.value - (series.stdDev || 0));
      maxAbs = Math.max(maxAbs, upper, lower);
    });
  });
  
  const rangeWithPadding = maxAbs * 1.2;
  
  let range;
  if (rangeWithPadding <= 5) {
    range = Math.ceil(rangeWithPadding);
  } else if (rangeWithPadding <= 10) {
    range = Math.ceil(rangeWithPadding / 5) * 5;
  } else if (rangeWithPadding <= 20) {
    range = Math.ceil(rangeWithPadding / 5) * 5;
  } else if (rangeWithPadding <= 50) {
    range = Math.ceil(rangeWithPadding / 10) * 10;
  } else {
    range = Math.ceil(rangeWithPadding / 20) * 20;
  }
  
  return [-range, range];
};

// Helper to generate Y-axis ticks that are perfectly symmetrical around 0
const generateYTicks = (domain) => {
  const [min, max] = domain;
  const range = max - min;
  
  let step;
  if (range <= 10) {
    step = 2;
  } else if (range <= 20) {
    step = 5;
  } else if (range <= 50) {
    step = 10;
  } else if (range <= 100) {
    step = 20;
  } else {
    step = 50;
  }
  
  const ticks = [0];
  
  for (let i = step; i <= max; i += step) {
    ticks.push(i);
  }
  
  for (let i = -step; i >= min; i -= step) {
    ticks.unshift(i);
  }
  
  return ticks;
};

const VolumeDeltaOverview = ({ sessions, filteredSessions, timeMode, onCellClick }) => {
  // Calculate balance
  const balanceValue = timeMode === 'trend'
    ? calculateTrend(filteredSessions, 'balance')
    : { simple: calculateAverage(filteredSessions, 'balance') };

  // Get loudest/quietest fingers
  const leftFingers = getLoudestQuietestFingers(filteredSessions, 'left');
  const rightFingers = getLoudestQuietestFingers(filteredSessions, 'right');

  // Calculate least consistent finger (by avg std dev across fingers)
  const getLeastConsistentFinger = (sessions, hand) => {
    const fingerStdDevs = [];
    
    for (let i = 1; i <= 5; i++) {
      const field = `${hand}_finger_${i}_relative_volume_std_dev`;
      const avgStdDev = calculateAverage(sessions, field);
      if (avgStdDev !== null) {
        fingerStdDevs.push({ finger: i, avgStdDev });
      }
    }
    
    if (fingerStdDevs.length === 0) return null;
    
    // Sort by avgStdDev descending (highest = least consistent)
    fingerStdDevs.sort((a, b) => b.avgStdDev - a.avgStdDev);
    
    return fingerStdDevs[0]; // Return the least consistent (highest std dev)
  };
  
  const leftLeastConsistent = getLeastConsistentFinger(filteredSessions, 'left');
  const rightLeastConsistent = getLeastConsistentFinger(filteredSessions, 'right');

  // Prepare data for charts (always use all 30 days)
  const leftTrendTime = prepareTrendOverTimeData(
    sessions,
    'left',
    [leftFingers.loudest?.finger, leftFingers.quietest?.finger].filter(Boolean)
  );
  const rightTrendTime = prepareTrendOverTimeData(
    sessions,
    'right',
    [rightFingers.loudest?.finger, rightFingers.quietest?.finger].filter(Boolean)
  );

  const leftTrendTempo = prepareTrendOverTempoData(
    sessions,
    'left',
    [leftFingers.loudest?.finger, leftFingers.quietest?.finger].filter(Boolean)
  );
  const rightTrendTempo = prepareTrendOverTempoData(
    sessions,
    'right',
    [rightFingers.loudest?.finger, rightFingers.quietest?.finger].filter(Boolean)
  );

  const leftTrendRepetition = prepareTrendOverRepetitionData(
    sessions,
    'left',
    [leftFingers.loudest?.finger, leftFingers.quietest?.finger].filter(Boolean)
  );
  const rightTrendRepetition = prepareTrendOverRepetitionData(
    sessions,
    'right',
    [rightFingers.loudest?.finger, rightFingers.quietest?.finger].filter(Boolean)
  );

  // Colors - match left (purple) and right (orange) from worst offenders
  const leftColor = '#9333ea';
  const rightColor = '#ea580c';
  const purpleShades = ['#6b21a8', '#9333ea', '#a855f7', '#c084fc'];
  const orangeShades = ['#ea580c', '#f97316', '#fb923c', '#fdba74']; // More orange, less red

  // Create regression lines with std dev for time chart
  const timeRegressionData = [];
  Object.entries(leftTrendTime).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = (maxX - minX) / 20;
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          timestamp: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      timeRegressionData.push({
        finger: `L${finger}`,
        color: purpleShades[idx % purpleShades.length],
        data: points,
        stdDev
      });
    }
  });
  
  Object.entries(rightTrendTime).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = (maxX - minX) / 20;
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          timestamp: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      timeRegressionData.push({
        finger: `R${finger}`,
        color: orangeShades[idx % orangeShades.length],
        data: points,
        stdDev
      });
    }
  });
  
  // Create regression lines with std dev for tempo chart
  const tempoRegressionData = [];
  Object.entries(leftTrendTempo).forEach(([finger, data], idx) => {
    if (data.points.length > 1) {
      const regression = calculateLinearRegression(data.points);
      if (regression) {
        const values = data.points.map(p => p.y);
        const stdDev = calculateStdDev(values);
        const minX = Math.min(...data.points.map(p => p.x));
        const maxX = Math.max(...data.points.map(p => p.x));
        
        const points = [];
        const step = (maxX - minX) / 20;
        for (let x = minX; x <= maxX; x += step) {
          const y = regression.slope * x + regression.intercept;
          points.push({
            tempo: x,
            value: y,
            upper: y + stdDev,
            lower: y - stdDev
          });
        }
        
        tempoRegressionData.push({
          finger: `L${finger}`,
          color: purpleShades[idx % purpleShades.length],
          data: points,
          stdDev
        });
      }
    }
  });
  
  Object.entries(rightTrendTempo).forEach(([finger, data], idx) => {
    if (data.points.length > 1) {
      const regression = calculateLinearRegression(data.points);
      if (regression) {
        const values = data.points.map(p => p.y);
        const stdDev = calculateStdDev(values);
        const minX = Math.min(...data.points.map(p => p.x));
        const maxX = Math.max(...data.points.map(p => p.x));
        
        const points = [];
        const step = (maxX - minX) / 20;
        for (let x = minX; x <= maxX; x += step) {
          const y = regression.slope * x + regression.intercept;
          points.push({
            tempo: x,
            value: y,
            upper: y + stdDev,
            lower: y - stdDev
          });
        }
        
        tempoRegressionData.push({
          finger: `R${finger}`,
          color: orangeShades[idx % orangeShades.length],
          data: points,
          stdDev
        });
      }
    }
  });

  // Create regression lines with std dev for repetition chart
  const repetitionRegressionData = [];
  Object.entries(leftTrendRepetition).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = Math.max(1, (maxX - minX) / 20);
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          repetition: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      repetitionRegressionData.push({
        finger: `L${finger}`,
        color: purpleShades[idx % purpleShades.length],
        data: points,
        stdDev
      });
    }
  });
  
  Object.entries(rightTrendRepetition).forEach(([finger, data], idx) => {
    if (data.regression && data.points.length > 0) {
      const values = data.points.map(p => p.y);
      const stdDev = calculateStdDev(values);
      const minX = Math.min(...data.points.map(p => p.x));
      const maxX = Math.max(...data.points.map(p => p.x));
      
      const points = [];
      const step = Math.max(1, (maxX - minX) / 20);
      for (let x = minX; x <= maxX; x += step) {
        const y = data.regression.slope * x + data.regression.intercept;
        points.push({
          repetition: x,
          value: y,
          upper: y + stdDev,
          lower: y - stdDev
        });
      }
      
      repetitionRegressionData.push({
        finger: `R${finger}`,
        color: orangeShades[idx % orangeShades.length],
        data: points,
        stdDev
      });
    }
  });

  // Calculate symmetric Y domains based on regression + std dev
  const timeYDomain = getSymmetricYDomainForRegression(timeRegressionData);
  const tempoYDomain = getSymmetricYDomainForRegression(tempoRegressionData);
  const repetitionYDomain = getSymmetricYDomainForRegression(repetitionRegressionData);

  // Custom legend component for side placement
  const renderCustomLegend = (data) => {
    return (
      <div className="flex flex-col gap-1 text-[9px]">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-1">
            <div className="w-2 h-0.5" style={{ backgroundColor: item.color }}></div>
            <span>{item.finger}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex gap-3">
      {/* Left Side: Balance, Least Consistent, and Worst Offenders */}
      <div className="flex flex-col gap-3" style={{ width: '350px' }}>
        {/* Top Row: Balance and Least Consistent side by side */}
        <div className="flex gap-3">
          {/* Balance */}
          <div className="bg-gray-50 p-2 rounded flex-1">
            <div className="text-xs font-semibold text-gray-600 mb-1">Balance</div>
            {timeMode === 'trend' ? (
              <div className="text-[10px] space-y-0.5">
                <div className="flex justify-between">
                  <span className="text-gray-600">5d:</span>
                  <span className="font-medium">{balanceValue.last5?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">30d:</span>
                  <span className="font-medium">{balanceValue.last30?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex justify-between pt-0.5 border-t border-gray-300">
                  <span className="text-gray-600 font-semibold">Δ:</span>
                  <span className={`font-bold ${balanceValue.difference > 0 ? 'text-green-600' : 'text-blue-600'}`}>
                    {balanceValue.difference?.toFixed(1) || 'N/A'}
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-xs font-medium">
                {formatBalance(balanceValue.simple)}
              </p>
            )}
          </div>

          {/* Least Consistent */}
          <div className="bg-gray-50 p-2 rounded flex-1">
            <div className="text-xs font-semibold text-gray-600 mb-1">Least Consistent</div>
            <div className="flex items-center justify-between text-[10px] py-1">
              <div 
                className="flex items-center gap-1 cursor-pointer hover:bg-purple-100 px-2 py-1 rounded transition-colors"
                onClick={() => leftLeastConsistent && onCellClick && onCellClick({
                  entity: `left_finger_${leftLeastConsistent.finger}`,
                  exercise: null,
                  exerciseType: null
                })}
              >
                <span className="font-semibold" style={{ color: '#9333ea' }}>Left:</span>
                <span className="font-bold text-gray-900">
                  {leftLeastConsistent ? leftLeastConsistent.finger : 'N/A'}
                </span>
              </div>
              <div 
                className="flex items-center gap-1 cursor-pointer hover:bg-orange-100 px-2 py-1 rounded transition-colors"
                onClick={() => rightLeastConsistent && onCellClick && onCellClick({
                  entity: `right_finger_${rightLeastConsistent.finger}`,
                  exercise: null,
                  exerciseType: null
                })}
              >
                <span className="font-semibold" style={{ color: '#ea580c' }}>Right:</span>
                <span className="font-bold text-gray-900">
                  {rightLeastConsistent ? rightLeastConsistent.finger : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row: Worst Offenders spanning full width */}
        <div className="bg-gray-50 p-2 rounded">
          <div className="text-xs font-semibold text-gray-600 mb-1">Worst Offenders</div>
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-0.5"></th>
                <th className="text-center py-0.5 font-semibold" style={{ color: '#9333ea' }}>Left</th>
                <th className="text-center py-0.5 font-semibold" style={{ color: '#ea580c' }}>Right</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-0.5 font-semibold text-gray-600">Loud</td>
                <td 
                  className="text-center py-0.5 cursor-pointer hover:bg-purple-100 transition-colors"
                  onClick={() => leftFingers.loudest && onCellClick && onCellClick({
                    entity: `left_finger_${leftFingers.loudest.finger}`,
                    exercise: null,
                    exerciseType: null
                  })}
                >
                  <span className="font-bold" style={{ color: '#9333ea' }}>
                    {leftFingers.loudest?.finger || '-'}
                  </span>
                  <span className="text-gray-500 ml-1 text-[9px]">
                    ({leftFingers.loudest?.avg?.toFixed(1) || 'N/A'})
                  </span>
                </td>
                <td 
                  className="text-center py-0.5 cursor-pointer hover:bg-orange-100 transition-colors"
                  onClick={() => rightFingers.loudest && onCellClick && onCellClick({
                    entity: `right_finger_${rightFingers.loudest.finger}`,
                    exercise: null,
                    exerciseType: null
                  })}
                >
                  <span className="font-bold" style={{ color: '#ea580c' }}>
                    {rightFingers.loudest?.finger || '-'}
                  </span>
                  <span className="text-gray-500 ml-1 text-[9px]">
                    ({rightFingers.loudest?.avg?.toFixed(1) || 'N/A'})
                  </span>
                </td>
              </tr>
              <tr>
                <td className="py-0.5 font-semibold text-gray-600">Quiet</td>
                <td 
                  className="text-center py-0.5 cursor-pointer hover:bg-purple-100 transition-colors"
                  onClick={() => leftFingers.quietest && onCellClick && onCellClick({
                    entity: `left_finger_${leftFingers.quietest.finger}`,
                    exercise: null,
                    exerciseType: null
                  })}
                >
                  <span className="font-bold" style={{ color: '#9333ea' }}>
                    {leftFingers.quietest?.finger || '-'}
                  </span>
                  <span className="text-gray-500 ml-1 text-[9px]">
                    ({leftFingers.quietest?.avg?.toFixed(1) || 'N/A'})
                  </span>
                </td>
                <td 
                  className="text-center py-0.5 cursor-pointer hover:bg-orange-100 transition-colors"
                  onClick={() => rightFingers.quietest && onCellClick && onCellClick({
                    entity: `right_finger_${rightFingers.quietest.finger}`,
                    exercise: null,
                    exerciseType: null
                  })}
                >
                  <span className="font-bold" style={{ color: '#ea580c' }}>
                    {rightFingers.quietest?.finger || '-'}
                  </span>
                  <span className="text-gray-500 ml-1 text-[9px]">
                    ({rightFingers.quietest?.avg?.toFixed(1) || 'N/A'})
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Right Side: Graphs - Horizontal Layout (2 columns) */}
      <div className="flex-1 grid grid-cols-2 gap-2">
        {/* Trend over Tempo */}
        <div className="bg-gray-50 p-2 rounded">
          <h3 className="text-[10px] font-semibold text-gray-700 mb-1">Trend by Tempo</h3>
          {tempoRegressionData.length > 0 ? (
            <div className="flex gap-1">
              <ResponsiveContainer width="80%" height={120}>
                <ComposedChart margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                  <defs>
                    {tempoRegressionData.map((regData, idx) => (
                      <linearGradient key={`gradient-tempo-${idx}`} id={`gradient-tempo-${idx}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={regData.color} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={regData.color} stopOpacity={0.1} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                  <ReferenceArea y1={-2} y2={2} fill="#10b981" fillOpacity={0.1} />
                  <XAxis 
                    type="number"
                    dataKey="tempo" 
                    domain={['dataMin', 'dataMax']}
                    allowDataOverflow={false}
                    tick={{ fontSize: 8 }}
                    tickFormatter={(value) => Math.round(value)}
                  />
                  <YAxis 
                    type="number"
                    dataKey="value"
                    domain={tempoYDomain}
                    ticks={generateYTicks(tempoYDomain)}
                    tick={{ fontSize: 8 }}
                    width={25}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-1 border border-gray-300 rounded shadow-sm">
                            <p className="text-[9px]">BPM: {Math.round(data.tempo)}</p>
                            <p className="text-[9px]">Val: {data.value?.toFixed(2)}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Raw scatter points for left fingers */}
                  {Object.entries(leftTrendTempo).map(([finger, data], idx) => {
                    if (!data.points || data.points.length === 0) return null;
                    // Calculate opacity based on distance from ideal range (-2 to 2)
                    const dataWithOpacity = data.points.map(p => {
                      const absValue = Math.abs(p.y);
                      let opacity;
                      if (absValue <= 2) {
                        opacity = 0.8; // In range - most visible
                      } else if (absValue <= 5) {
                        opacity = 0.6; // Slightly out of range
                      } else if (absValue <= 10) {
                        opacity = 0.4; // More out of range
                      } else {
                        opacity = 0.2; // Far out of range
                      }
                      return { tempo: p.x, value: p.y, opacity };
                    });
                    return (
                      <Scatter
                        key={`left-tempo-scatter-${finger}`}
                        data={dataWithOpacity}
                        fill={leftColor}
                        fillOpacity={(entry) => entry.opacity || 0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    );
                  })}
                  {/* Raw scatter points for right fingers */}
                  {Object.entries(rightTrendTempo).map(([finger, data], idx) => {
                    if (!data.points || data.points.length === 0) return null;
                    // Calculate opacity based on distance from ideal range (-2 to 2)
                    const dataWithOpacity = data.points.map(p => {
                      const absValue = Math.abs(p.y);
                      let opacity;
                      if (absValue <= 2) {
                        opacity = 0.8; // In range - most visible
                      } else if (absValue <= 5) {
                        opacity = 0.6; // Slightly out of range
                      } else if (absValue <= 10) {
                        opacity = 0.4; // More out of range
                      } else {
                        opacity = 0.2; // Far out of range
                      }
                      return { tempo: p.x, value: p.y, opacity };
                    });
                    return (
                      <Scatter
                        key={`right-tempo-scatter-${finger}`}
                        data={dataWithOpacity}
                        fill={rightColor}
                        fillOpacity={(entry) => entry.opacity || 0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    );
                  })}
                  {/* Regression lines */}
                  {tempoRegressionData.map((regData, idx) => (
                    <React.Fragment key={`series-tempo-${idx}`}>
                      <Area
                        data={regData.data}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill={`url(#gradient-tempo-${idx})`}
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={regData.data}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={regData.data}
                        type="monotone"
                        dataKey="value"
                        stroke={regData.color}
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </React.Fragment>
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
              {renderCustomLegend(tempoRegressionData)}
            </div>
          ) : (
            <p className="text-[9px] text-gray-500 text-center py-4">No data</p>
          )}
        </div>

        {/* Trend over Repetition */}
        <div className="bg-gray-50 p-2 rounded">
          <h3 className="text-[10px] font-semibold text-gray-700 mb-1">Trend by Repetition</h3>
          {repetitionRegressionData.length > 0 ? (
            <div className="flex gap-1">
              <ResponsiveContainer width="80%" height={120}>
                <ComposedChart margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
                  <defs>
                    {repetitionRegressionData.map((regData, idx) => (
                      <linearGradient key={`gradient-rep-${idx}`} id={`gradient-rep-${idx}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={regData.color} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={regData.color} stopOpacity={0.1} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
                  <ReferenceArea y1={-2} y2={2} fill="#10b981" fillOpacity={0.1} />
                  <XAxis 
                    type="number"
                    dataKey="repetition" 
                    domain={['dataMin', 'dataMax']}
                    allowDataOverflow={false}
                    hide
                  />
                  <YAxis 
                    type="number"
                    dataKey="value"
                    domain={repetitionYDomain}
                    ticks={generateYTicks(repetitionYDomain)}
                    tick={{ fontSize: 8 }}
                    width={25}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-white p-1 border border-gray-300 rounded shadow-sm">
                            <p className="text-[9px]">Rep: #{Math.round(data.repetition)}</p>
                            <p className="text-[9px]">Val: {data.value?.toFixed(2)}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  {/* Raw scatter points for left fingers */}
                  {Object.entries(leftTrendRepetition).map(([finger, data], idx) => {
                    if (!data.points || data.points.length === 0) return null;
                    // Calculate opacity based on distance from ideal range (-2 to 2)
                    const dataWithOpacity = data.points.map(p => {
                      const absValue = Math.abs(p.y);
                      let opacity;
                      if (absValue <= 2) {
                        opacity = 0.8; // In range - most visible
                      } else if (absValue <= 5) {
                        opacity = 0.6; // Slightly out of range
                      } else if (absValue <= 10) {
                        opacity = 0.4; // More out of range
                      } else {
                        opacity = 0.2; // Far out of range
                      }
                      return { repetition: p.x, value: p.y, opacity };
                    });
                    return (
                      <Scatter
                        key={`left-rep-scatter-${finger}`}
                        data={dataWithOpacity}
                        fill={leftColor}
                        fillOpacity={(entry) => entry.opacity || 0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    );
                  })}
                  {/* Raw scatter points for right fingers */}
                  {Object.entries(rightTrendRepetition).map(([finger, data], idx) => {
                    if (!data.points || data.points.length === 0) return null;
                    // Calculate opacity based on distance from ideal range (-2 to 2)
                    const dataWithOpacity = data.points.map(p => {
                      const absValue = Math.abs(p.y);
                      let opacity;
                      if (absValue <= 2) {
                        opacity = 0.8; // In range - most visible
                      } else if (absValue <= 5) {
                        opacity = 0.6; // Slightly out of range
                      } else if (absValue <= 10) {
                        opacity = 0.4; // More out of range
                      } else {
                        opacity = 0.2; // Far out of range
                      }
                      return { repetition: p.x, value: p.y, opacity };
                    });
                    return (
                      <Scatter
                        key={`right-rep-scatter-${finger}`}
                        data={dataWithOpacity}
                        fill={rightColor}
                        fillOpacity={(entry) => entry.opacity || 0.6}
                        r={3}
                        isAnimationActive={false}
                      />
                    );
                  })}
                  {/* Regression lines */}
                  {repetitionRegressionData.map((regData, idx) => (
                    <React.Fragment key={`series-rep-${idx}`}>
                      <Area
                        data={regData.data}
                        type="monotone"
                        dataKey="upper"
                        stroke="none"
                        fill={`url(#gradient-rep-${idx})`}
                        fillOpacity={0.3}
                        isAnimationActive={false}
                      />
                      <Area
                        data={regData.data}
                        type="monotone"
                        dataKey="lower"
                        stroke="none"
                        fill="transparent"
                        isAnimationActive={false}
                      />
                      <Line
                        data={regData.data}
                        type="monotone"
                        dataKey="value"
                        stroke={regData.color}
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    </React.Fragment>
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
              {renderCustomLegend(repetitionRegressionData)}
            </div>
          ) : (
            <p className="text-[9px] text-gray-500 text-center py-4">No data</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VolumeDeltaOverview;
